import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, X, CreditCard, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { formatCurrency } from '@/lib/utils'
import type { APIResponse, AppliedCardPromoState, EligibleCardPromo } from '@/types'

interface CounterCardPromoBlockProps {
  orderId: string
  /**
   * Only rendered meaningfully when the checkout intent is 'card' — the
   * parent hides the block otherwise (a card promo requires a card tender;
   * the backend enforces the same server-side).
   */
  visible: boolean
  /** Called on apply/remove so the parent re-fetches the order totals. */
  onChanged?: () => void
}

/**
 * Quick-pick surface for bank card promos ("20% off with HBL Platinum").
 * Sits directly under the tender Method grid — cashier speed is the whole
 * point: tap Card, tap the matching promo chip, done.
 *
 * Two states:
 *   - none applied: one chip per promo valid RIGHT NOW (server filters by
 *     active/date/day-of-week/min-bill), showing the projected rupee off
 *   - applied: banner with the promo label + Remove
 *
 * Renders nothing when no promos are eligible and none is applied, so
 * tenants without bank deals never see the section.
 */
export function CounterCardPromoBlock({ orderId, visible, onChanged }: CounterCardPromoBlockProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const appliedQuery = useQuery<APIResponse<AppliedCardPromoState>>({
    queryKey: ['counter-applied-card-promo', orderId],
    queryFn: () => apiClient.getAppliedCardPromo(orderId),
    enabled: Boolean(orderId),
    staleTime: 5_000,
  })
  const applied = appliedQuery.data?.data

  const eligibleQuery = useQuery<APIResponse<EligibleCardPromo[]>>({
    queryKey: ['counter-eligible-card-promos', orderId],
    queryFn: () => apiClient.getEligibleCardPromos(orderId),
    // Only fetch while the picker is actually on screen and nothing applied.
    enabled: Boolean(orderId) && visible && !applied?.applied,
    staleTime: 15_000,
  })
  const eligible = eligibleQuery.data?.data ?? []

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['counter-applied-card-promo', orderId] })
    queryClient.invalidateQueries({ queryKey: ['counter-eligible-card-promos', orderId] })
  }

  const applyMutation = useMutation({
    mutationFn: (cardPromoId: string) => apiClient.applyCardPromoToOrder(orderId, cardPromoId),
    onSuccess: (res) => {
      const data = res.data
      toast({
        title: 'Card promo applied',
        description: data
          ? `${data.card_label ?? data.bank_name} · ${formatCurrency(data.discount_applied ?? 0)} off`
          : undefined,
        variant: 'success',
      })
      invalidate()
      onChanged?.()
    },
    onError: (err) => surfaceCardPromoError(err, toast),
  })

  const removeMutation = useMutation({
    mutationFn: () => apiClient.removeCardPromoFromOrder(orderId),
    onSuccess: () => {
      toast({ title: 'Card promo removed', variant: 'success' })
      invalidate()
      onChanged?.()
    },
    onError: (err) => surfaceCardPromoError(err, toast),
  })

  const busy = applyMutation.isPending || removeMutation.isPending

  if (!visible && !applied?.applied) return null

  if (applied?.applied) {
    return (
      <div className="space-y-1.5">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Bank card promo
        </div>
        <div className="flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50/60 px-2 py-1.5 dark:border-sky-900/60 dark:bg-sky-950/40">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300">
            <Check className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className="border-sky-300 bg-sky-50 text-sky-800 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-200 text-xs"
              >
                {applied.card_label ?? applied.bank_name}
              </Badge>
              <span className="text-xs font-medium tabular-nums text-sky-700 dark:text-sky-300">
                −{formatCurrency(applied.discount_applied ?? 0)}
              </span>
            </div>
            {applied.label ? (
              <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{applied.label}</div>
            ) : null}
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => removeMutation.mutate()}
            disabled={busy}
            className="h-8 gap-1 text-xs text-destructive hover:text-destructive"
          >
            {removeMutation.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <X className="h-3 w-3" />
            )}
            Remove
          </Button>
        </div>
      </div>
    )
  }

  if (eligibleQuery.isLoading || eligible.length === 0) return null

  return (
    <div className="space-y-1.5">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Bank card promo
      </div>
      <div className="flex flex-wrap gap-1.5">
        {eligible.map((p) => (
          <button
            key={p.id}
            type="button"
            disabled={busy}
            onClick={() => applyMutation.mutate(p.id)}
            className="flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50/80 px-2.5 py-1.5 text-left text-xs font-medium text-sky-950 transition-colors hover:border-sky-300 hover:bg-sky-100 disabled:opacity-60 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-50 dark:hover:bg-sky-900/50"
          >
            {applyMutation.isPending && applyMutation.variables === p.id ? (
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
            ) : (
              <CreditCard className="h-3.5 w-3.5 shrink-0" aria-hidden />
            )}
            <span className="truncate">{p.card_label}</span>
            <span className="shrink-0 tabular-nums text-sky-700 dark:text-sky-300">
              {p.discount_percent}% (−{formatCurrency(p.projected_discount)})
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

function surfaceCardPromoError(err: unknown, toast: ReturnType<typeof useToast>['toast']) {
  // F-DEAL-N3: best single discount wins — the server explains which deal
  // already saves more and by how much.
  if (err && typeof err === 'object' && (err as { code?: unknown }).code === 'better_auto_discount') {
    toast({ title: 'The deal already saves more', description: err instanceof Error ? err.message : '' })
    return
  }
  const reason = extractCardPromoErrorReason(err)
  const map: Record<string, { title: string; description: string }> = {
    inactive: { title: 'Promo inactive', description: 'This promo has been archived.' },
    card_promo_expired: { title: 'Promo not valid today', description: 'Outside the promo date window.' },
    card_promo_wrong_day: { title: 'Wrong day', description: 'This promo does not run today.' },
    min_subtotal: { title: 'Order too small', description: 'Order subtotal is below the promo minimum.' },
    card_promo_requires_card_tender: {
      title: 'Card payment required',
      description: 'Select Card as the payment method first — this discount only applies to card payments.',
    },
    conflict_promo: { title: 'Promo code in place', description: 'Remove the promo code before applying a card promo.' },
    conflict_loyalty: { title: 'Loyalty reward in place', description: 'Remove the loyalty reward before applying a card promo.' },
    conflict_manual_discount: { title: 'Manual discount in place', description: 'Remove the manual discount before applying a card promo.' },
    conflict_existing_card_promo: { title: 'Card promo already applied', description: 'Remove the current card promo first.' },
    order_not_open: { title: 'Order is closed', description: 'Cannot edit a completed or cancelled order.' },
  }
  const tx =
    (reason && map[reason]) || {
      title: 'Card promo failed',
      description: err instanceof Error ? err.message : String(err),
    }
  toast({ title: tx.title, description: tx.description, variant: 'destructive' })
}

function extractCardPromoErrorReason(err: unknown): string | undefined {
  if (!err || typeof err !== 'object') return undefined
  const anyErr = err as { response?: { data?: { error?: string } } }
  return anyErr.response?.data?.error
}
